import React, { useState, useEffect } from 'react';
import { Search, CheckCircle, Eye, Download, Users, DollarSign, Trophy, Filter, Calendar } from 'lucide-react';

const LeaderRewardsAdmin = () => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReward, setSelectedReward] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchAllRewards();
  }, []);

  const fetchAllRewards = async () => {
    try {
      setLoading(true);
      const allRewards = [];
      
      // Fetch users directly - your structure shows users directly under root with date keys
      const usersRes = await fetch('https://aerovolt-db464-default-rtdb.firebaseio.com/users.json');
      const usersData = await usersRes.json();
      
      // Process users data
      if (usersData) {
        // Handle the date-based structure (2026-02-11 as key)
        Object.keys(usersData).forEach(dateKey => {
          const dateGroup = usersData[dateKey];
          if (dateGroup && typeof dateGroup === 'object') {
            
            Object.keys(dateGroup).forEach(userKey => {
              const user = dateGroup[userKey];
              
              // Check if user has leaderRewards object
              if (user && user.leaderRewards) {
                // Handle leaderRewards as an object with reward IDs as keys
                if (typeof user.leaderRewards === 'object') {
                  Object.keys(user.leaderRewards).forEach(rewardKey => {
                    const reward = user.leaderRewards[rewardKey];
                    
                    // Only include claimed rewards
                    if (reward && reward.status === 'claimed') {
                      allRewards.push({
                        ...reward,
                        userId: user.sponsorId || userKey,
                        userName: user.name || 'Unknown',
                        sponsorId: user.sponsorId,
                        mobile: user.mobile,
                        email: user.email,
                        claimedDate: reward.claimedAt,
                        type: 'leader', // Mark as leader reward
                        rewardAmount: reward.cashAmount || 0,
                        dateFolder: dateKey
                      });
                    }
                  });
                }
              }
              
              // Also check for teamRewards if they exist in your structure
              if (user && user.teamRewards && Array.isArray(user.teamRewards)) {
                user.teamRewards.forEach(reward => {
                  if (reward && reward.status === 'claimed') {
                    allRewards.push({
                      ...reward,
                      userId: user.sponsorId || userKey,
                      userName: user.name || 'Unknown',
                      sponsorId: user.sponsorId,
                      mobile: user.mobile,
                      email: user.email,
                      claimedDate: reward.claimedAt,
                      type: 'team',
                      dateFolder: dateKey,
                      rewardAmount: reward.cashAmount || 0
                    });
                  }
                });
              }
            });
          }
        });
      }
      
      // Sort by claim date (newest first)
      allRewards.sort((a, b) => new Date(b.claimedDate) - new Date(a.claimedDate));
      setRewards(allRewards);
      
    } catch (error) {
      console.error('Error fetching rewards:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRewards = rewards.filter(reward => {
    const matchesSearch = 
      reward.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reward.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reward.sponsorId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reward.mobile?.includes(searchTerm);
    
    const matchesType = filterType === 'all' || reward.type === filterType;
    
    return matchesSearch && matchesType;
  });

  const getStats = () => {
    const total = rewards.length;
    const totalAmount = rewards.reduce((sum, r) => sum + (r.rewardAmount || 0), 0);
    const leaderCount = rewards.filter(r => r.type === 'leader').length;
    const teamCount = rewards.filter(r => r.type === 'team').length;
    
    return { total, totalAmount, leaderCount, teamCount };
  };

  const stats = getStats();

  const exportToCSV = () => {
    const headers = ['Type', 'User', 'ID', 'Mobile', 'Reward', 'Amount', 'Date', 'Target', 'Team Sales'];
    const csvData = filteredRewards.map(r => [
      r.type === 'leader' ? 'Leader Reward' : 'Team Reward',
      r.userName || 'Unknown',
      r.sponsorId || r.userId,
      r.mobile || '',
      r.rewardName || 'Reward',
      r.rewardAmount || '0',
      r.claimedDate ? new Date(r.claimedDate).toLocaleDateString() : '',
      r.salesTarget || r.requirementAmount || '0',
      r.teamSales || '0'
    ]);
    
    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rewards_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading rewards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Claimed Rewards</h1>
        <p className="text-muted-foreground">View all user reward claims</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card-glow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Rewards</p>
              <p className="text-xl font-bold">{stats.total}</p>
            </div>
            <Trophy className="text-yellow-400" size={20} />
          </div>
        </div>

        <div className="card-glow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Amount</p>
              <p className="text-xl font-bold">${stats.totalAmount.toFixed(2)}</p>
            </div>
            <DollarSign className="text-green-400" size={20} />
          </div>
        </div>

        <div className="card-glow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Leader Rewards</p>
              <p className="text-xl font-bold">{stats.leaderCount}</p>
            </div>
            <Users className="text-blue-400" size={20} />
          </div>
        </div>

        <div className="card-glow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Team Rewards</p>
              <p className="text-xl font-bold">{stats.teamCount}</p>
            </div>
            <Trophy className="text-purple-400" size={20} />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
          <input
            type="text"
            placeholder="Search by name, ID, or mobile..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-lg"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterType('all')}
            className={`px-4 py-2 rounded-lg ${filterType === 'all' ? 'btn-electric' : 'bg-secondary text-muted-foreground'}`}
          >
            All ({rewards.length})
          </button>
          <button
            onClick={() => setFilterType('leader')}
            className={`px-4 py-2 rounded-lg ${filterType === 'leader' ? 'btn-electric' : 'bg-secondary text-muted-foreground'}`}
          >
            Leader ({stats.leaderCount})
          </button>
          <button
            onClick={() => setFilterType('team')}
            className={`px-4 py-2 rounded-lg ${filterType === 'team' ? 'btn-electric' : 'bg-secondary text-muted-foreground'}`}
          >
            Team ({stats.teamCount})
          </button>
          <button
            onClick={exportToCSV}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg"
          >
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Rewards List */}
      <div className="space-y-4">
        {filteredRewards.length === 0 ? (
          <div className="card-glow p-8 text-center">
            <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-bold text-foreground mb-2">No Rewards Found</h3>
            <p className="text-muted-foreground">
              {searchTerm ? 'Try different search terms' : 'No rewards have been claimed yet'}
            </p>
          </div>
        ) : (
          filteredRewards.map((reward, index) => (
            <div key={index} className="card-glow p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      reward.type === 'leader' 
                        ? 'bg-blue-500/20 text-blue-400' 
                        : reward.type === 'team'
                        ? 'bg-purple-500/20 text-purple-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {reward.type === 'leader' ? 'Leader Reward' : 'Team Reward'}
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">
                      <CheckCircle size={12} className="inline mr-1" />
                      Claimed
                    </span>
                  </div>
                  <h3 className="font-bold text-foreground">{reward.rewardName || 'Leader Reward'}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {reward.userName} • {reward.sponsorId}
                  </p>
                  {reward.mobile && (
                    <p className="text-xs text-muted-foreground">{reward.mobile}</p>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-yellow-400">
                    ${(reward.rewardAmount || 0).toFixed(2)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {reward.claimedDate ? new Date(reward.claimedDate).toLocaleDateString() : ''}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <div className="text-muted-foreground">
                  Target: ${reward.salesTarget || reward.requirementAmount || 0}
                  {reward.teamSales && ` • Team Sales: $${reward.teamSales}`}
                </div>
                <button
                  onClick={() => {
                    setSelectedReward(reward);
                    setShowDetails(true);
                  }}
                  className="flex items-center gap-1 text-primary hover:text-primary/80"
                >
                  <Eye size={16} />
                  Details
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Details Modal */}
      {showDetails && selectedReward && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-border flex justify-between items-center">
              <h3 className="text-lg font-bold">Reward Details</h3>
              <button onClick={() => setShowDetails(false)} className="text-muted-foreground hover:text-foreground">
                ✕
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">User</p>
                  <p className="font-bold">{selectedReward.userName}</p>
                  <p className="text-sm text-muted-foreground">ID: {selectedReward.sponsorId}</p>
                  {selectedReward.mobile && (
                    <p className="text-sm text-muted-foreground">Mobile: {selectedReward.mobile}</p>
                  )}
                  {selectedReward.email && (
                    <p className="text-sm text-muted-foreground">Email: {selectedReward.email}</p>
                  )}
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">Reward</p>
                  <p className="font-bold">{selectedReward.rewardName || 'Leader Reward'}</p>
                  <span className={`text-xs px-2 py-1 rounded-full inline-block mt-1 ${
                    selectedReward.type === 'leader' 
                      ? 'bg-blue-500/20 text-blue-400' 
                      : 'bg-purple-500/20 text-purple-400'
                  }`}>
                    {selectedReward.type === 'leader' ? 'Leader Reward' : 'Team Reward'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Amount</p>
                    <p className="text-lg font-bold text-yellow-400">
                      ${(selectedReward.rewardAmount || 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Target</p>
                    <p className="text-lg font-bold">
                      ${selectedReward.salesTarget || selectedReward.requirementAmount || 0}
                    </p>
                  </div>
                </div>

                {selectedReward.teamSales && (
                  <div>
                    <p className="text-sm text-muted-foreground">Team Sales</p>
                    <p className="font-medium">${selectedReward.teamSales}</p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-muted-foreground">Claimed Date</p>
                  <p className="font-medium">
                    {selectedReward.claimedDate ? new Date(selectedReward.claimedDate).toLocaleString() : 'N/A'}
                  </p>
                </div>

                {selectedReward.timeframe && (
                  <div>
                    <p className="text-sm text-muted-foreground">Timeframe</p>
                    <p className="font-medium capitalize">{selectedReward.timeframe}</p>
                  </div>
                )}

                {selectedReward.downlineActivationStatusUpdatedBy && (
                  <div>
                    <p className="text-sm text-muted-foreground">Updated By</p>
                    <p className="font-medium">{selectedReward.downlineActivationStatusUpdatedBy}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-border">
              <button
                onClick={() => setShowDetails(false)}
                className="w-full py-3 btn-electric rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaderRewardsAdmin;
